import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminSectionSponsors, adminUpsertSectionSponsor, adminDeleteSectionSponsor } from "../lib/api";
import { Toolbar, Card, Field, inputCls, Check } from "./AdminLayout";
import ImageUpload from "../components/ImageUpload";

const SECTIONS = [
  { key: "home", label: "Home / Front Page" },
  { key: "news", label: "Local News" },
  { key: "sports", label: "High School Sports" },
  { key: "entertainment", label: "Entertainment" },
  { key: "events", label: "Events Calendar" },
];

export default function SponsorsAdmin() {
  const [map, setMap] = useState({});
  const load = () =>
    adminSectionSponsors().then((list) => {
      const m = {};
      (list || []).forEach((s) => { m[s.section] = s; });
      setMap(m);
    }).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <div>
      <Toolbar title="Section Sponsors" subtitle="Give an advertiser fixed logo placement beside a section headline — shown on both the broadsheet and the Nassau Life portal" />
      <div className="grid gap-5 lg:grid-cols-2">
        {SECTIONS.map((s) => <SponsorCard key={s.key} section={s} current={map[s.key]} onSaved={load} />)}
      </div>
    </div>
  );
}

function SponsorCard({ section, current, onSaved }) {
  const [form, setForm] = useState({ sponsor_name: "", logo_url: "", link: "", message: "", offer: "", offer_link: "", active: true });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (current) setForm({ sponsor_name: current.sponsor_name || "", logo_url: current.logo_url || "", link: current.link || "", message: current.message || "", offer: current.offer || "", offer_link: current.offer_link || "", active: current.active !== false });
  }, [current]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.sponsor_name && !form.logo_url) return toast.error("Add a sponsor name or logo.");
    setBusy(true);
    try { await adminUpsertSectionSponsor(section.key, form); toast.success(`${section.label} sponsor saved.`); onSaved(); }
    catch { toast.error("Could not save."); }
    finally { setBusy(false); }
  };
  const clear = async () => {
    await adminDeleteSectionSponsor(section.key);
    toast.success("Sponsor cleared.");
    setForm({ sponsor_name: "", logo_url: "", link: "", message: "", offer: "", offer_link: "", active: true });
    onSaved();
  };

  return (
    <Card className="p-5" data-testid={`sponsor-card-${section.key}`}>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-bold text-[var(--navy)]">{section.label}</h3>
        {current && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Sponsored</span>}
      </div>
      {form.logo_url && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Preview</span>
          <img src={form.logo_url} alt="logo" className="h-7 max-w-[160px] object-contain" />
        </div>
      )}
      <div className="grid gap-4">
        <Field label="Sponsor name"><input data-testid={`sponsor-name-${section.key}`} value={form.sponsor_name} onChange={set("sponsor_name")} className={inputCls} placeholder="Salt & Tide Oyster Bar" /></Field>
        <Field label="Sponsor logo"><ImageUpload value={form.logo_url} onChange={(url) => setForm((f) => ({ ...f, logo_url: url }))} testid={`sponsor-logo-${section.key}`} /></Field>
        <Field label="Link (website)"><input data-testid={`sponsor-link-${section.key}`} value={form.link} onChange={set("link")} className={inputCls} placeholder="https://" /></Field>
        <Field label="Promotional message (shown next to the logo)"><input data-testid={`sponsor-message-${section.key}`} value={form.message} onChange={set("message")} className={inputCls} placeholder="Proudly serving Nassau County" /></Field>
        <Field label="Special offer (Nassau Life header, next to the logo)"><input data-testid={`sponsor-offer-${section.key}`} value={form.offer} onChange={set("offer")} className={inputCls} placeholder="Shop today's BOGO here!" /></Field>
        <Field label="Offer link (optional, defaults to website)"><input data-testid={`sponsor-offer-link-${section.key}`} value={form.offer_link} onChange={set("offer_link")} className={inputCls} placeholder="https://" /></Field>
        <Check label="Active (show on site)" checked={form.active} onChange={() => setForm((f) => ({ ...f, active: !f.active }))} testid={`sponsor-active-${section.key}`} />
      </div>
      <div className="mt-4 flex justify-end gap-2">
        {current && <button onClick={clear} data-testid={`sponsor-clear-${section.key}`} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">Clear</button>}
        <button onClick={save} disabled={busy} data-testid={`sponsor-save-${section.key}`} className="rounded-lg bg-[var(--navy)] px-5 py-2 text-sm font-semibold text-white hover:bg-[var(--header-navy)] disabled:opacity-60">{busy ? "Saving…" : "Save"}</button>
      </div>
    </Card>
  );
}
